import { useFormContext } from '@iheartjane/reefer-hook-form';
import type {
  BulkPricingRulesPayload,
  ProductThreshold,
  ProductWeight,
  Schedule,
  SpecialRulesPayload,
  SpecialRulesPayloadFor,
  SpecialType,
  SpecialTypeWithRules,
} from '@iheartjane/types';
import { useIsMutating } from '@tanstack/react-query';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import { useEffect, useRef, useState } from 'react';

import type {
  FormExclusion,
  FormInclusion,
  FormRules,
  GlobalSpecialV2,
  SpecialRulesV2,
  StoreSpecialV2,
} from '@jane/business-admin/types';
import { BULK_PRICING_RULE_KEYS, RULE_KEYS } from '@jane/business-admin/types';
import {
  buildRules,
  sanitizeRules,
  transformRules,
} from '@jane/business-admin/util';
import type {
  ConditionSet,
  ConditionSetsByKey,
} from '@jane/shared-b2b/components';
import { getConditionEditors } from '@jane/shared-b2b/components';

import { foldRedundantExclusions } from './conditions/utils/foldRedundantExclusions';

export type ApplyToOptions = 'ALL' | 'INDIVIDUAL' | 'CONDITIONS';

type SpecialFormRules = {
  apply_to: ApplyToOptions;
  rules: SpecialRulesV2;
};

export type SpecialFormDisplay = {
  bundle: {
    max_applications_per_cart?: number | null;
    max_number_of_discounted_products?: number | null;
    settings?: any;
    threshold_number_of_items_in_cart?: number | null;
  };
  default_rules?: SpecialFormRules | undefined;
  discounted_rules?: SpecialFormRules | undefined;
  promo_code_enabled: boolean;
  required_rules?: SpecialFormRules | undefined;
  spending_threshold: {
    allow_discounts_on_required_products?: boolean;
    cart_threshold?: number;
    copy_conditions_for_discounted_products?: boolean;
    exclude_product_specials_from_bundle_qualifiers?: boolean;
    max_applications_per_cart?: number;
    max_number_of_discounted_products?: number;
  };
};

export type SpecialFormDataBase = {
  /** form state only: the conditions editors' groups, converted to rule_sets on submit */
  conditionSets: ConditionSetsByKey;
  display: SpecialFormDisplay;
  end_date: string | null | undefined;
  has_schedule_overrides: boolean;
  hidden_from: boolean;
  name: string;
  photo: string[] | null | undefined;
  ruleSettings: RuleSettingsFormState;
  schedule: Schedule;
  special_type: SpecialType | '';
  start_date: string | null | undefined;
  use_store_close_time: boolean;
};

export const RULE_OPTIONS: {
  name: string;
  value: ApplyToOptions;
}[] = [
  {
    name: 'All products',
    value: 'ALL',
  },
  {
    name: 'Individual products',
    value: 'INDIVIDUAL',
  },
  {
    name: 'Conditions',
    value: 'CONDITIONS',
  },
];

export const MAX_MESSAGE_LENGTH = 300;
export const MAX_CUSTOM_BADGE_LENGTH = 20;
export const FORM_FIELD_DEFAULT_MARGIN = 24;

export enum SPECIAL_TYPES {
  BulkPricing = 'bulk_pricing',
  Bundle = 'bundle',
  CartTotal = 'cart_total',
  Product = 'product',
  QualifiedGroup = 'qualified_group',
  SpendingThreshold = 'spending_threshold',
}

export const SHOW_PROMO_CODE_SPECIAL_TYPES: SpecialType[] = [
  SPECIAL_TYPES.BulkPricing,
  SPECIAL_TYPES.CartTotal,
  SPECIAL_TYPES.Product,
  SPECIAL_TYPES.SpendingThreshold,
];

export enum ConditionType {
  Brands = 'brands',
  Categories = 'categories',
  ExcludeBrands = 'exclude brands',
  ExcludeCategories = 'exclude categories',
  ExcludeLineages = 'exclude lineages',
  ExcludeProducts = 'exclude products',
  ExcludeWeights = 'exclude weights',
  ExcludeWeightValues = 'exclude weight_values',
  Lineage = 'lineage',
  Price = 'price',
  Products = 'products',
  Weight = 'weight',
  WeightAndPrice = 'weight and price',
  WeightValues = 'weight_values',
}

export const EXCLUDE_TYPES = [
  ConditionType.ExcludeBrands,
  ConditionType.ExcludeCategories,
  ConditionType.ExcludeLineages,
  ConditionType.ExcludeProducts,
  ConditionType.ExcludeWeights,
  ConditionType.ExcludeWeightValues,
];

export const getBundleDisplay = (
  isCreateMode: boolean,
  special?: StoreSpecialV2 | GlobalSpecialV2
) => {
  if (isCreateMode) {
    return {
      threshold_number_of_items_in_cart: 1, // Minimum products requirement
      max_number_of_discounted_products: 1, // Max products discounted
      settings: {
        allow_discounts_on_required_products: false, // Allow qualifying products to be discounted
        copy_conditions_for_discounted_products: false,
        apply_target_price_per_unit: false,
      },
      max_applications_per_cart: 1, // Max uses per order
    };
  }

  if (special?.special_type === 'bundle') {
    const rulesSettings = special?.rules?.settings;

    return {
      threshold_number_of_items_in_cart:
        rulesSettings?.threshold_number_of_items_in_cart,
      settings: pick(rulesSettings, [
        'allow_discounts_on_required_products',
        'copy_conditions_for_discounted_products',
        'apply_target_price_per_unit',
        'exclude_product_specials_from_bundle_qualifiers',
      ]),
      max_number_of_discounted_products:
        rulesSettings?.max_number_of_discounted_products,
      max_applications_per_cart: rulesSettings?.max_applications_per_cart,
    };
  }
  return {};
};

export const getSpendingThresholdDisplay = (
  isCreateMode: boolean,
  special?: StoreSpecialV2 | GlobalSpecialV2
) => {
  if (isCreateMode) {
    return {
      cart_threshold: 1, // Minimum spend requirement
      max_number_of_discounted_products: 1, // Max products discounted
      max_applications_per_cart: 1, // Max uses per order
      allow_discounts_on_required_products: false, // Allow qualifying products to be discounted
      copy_conditions_for_discounted_products: false,
      exclude_product_specials_from_bundle_qualifiers: false,
    };
  }

  if (!special || special?.special_type !== 'spending_threshold') {
    return {};
  }

  const rules = special.rules;

  return {
    cart_threshold: rules?.settings?.cart_threshold,
    max_number_of_discounted_products:
      rules?.settings?.max_number_of_discounted_products,
    copy_conditions_for_discounted_products:
      rules?.settings?.copy_conditions_for_discounted_products,
    allow_discounts_on_required_products:
      rules?.settings?.allow_discounts_on_required_products,
    exclude_product_specials_from_bundle_qualifiers:
      rules?.settings?.exclude_product_specials_from_bundle_qualifiers,
    max_applications_per_cart: rules?.settings?.max_applications_per_cart,
  };
};

// Wide enough to also hold the create-mode defaults, which populate every special
// type's section at once (the type hasn't been chosen yet).
export type RuleSettingsFormState = {
  bulk_pricing?: {
    max_applications_per_cart?: number | null;
    target_weight?: string | null;
  };
  bundle?: {
    dependent?: Record<string, unknown>;
    independent?: Record<string, unknown>;
    max_applications_per_cart?: number | null;
    settings?: { allow_discounts_on_required_products?: boolean | null };
  };
  cart_total?: { threshold?: number | string | null };
  qualified_group?: {
    required_age?: number | null;
    type?: string | null;
    verification_type?: 'photo' | 'number' | null;
  };
};

export const VERIFICATION_TYPE_OPTIONS = [
  { id: 'photo', label: 'ID Photo Upload (Front and Back)', value: 'photo' },
  { id: 'number', label: 'Medical ID Number', value: 'number' },
];

export const getVerificationTypeLabel = (verificationType?: string | null) =>
  VERIFICATION_TYPE_OPTIONS.find(
    (option) => option.value === (verificationType || 'photo')
  )?.label || VERIFICATION_TYPE_OPTIONS[0].label;

// Keeps `<name>.verification_type` in sync with the selected qualified group:
// defaults to 'photo' when medical (and unset), and clears it when switching to
// any other group. Shared by the global and store QualifiedGroup forms.
export const useInitializeMedicalVerificationType = ({
  enabled,
  name,
  qualifiedGroupType,
}: {
  enabled: boolean;
  name: string;
  qualifiedGroupType?: string | null;
}) => {
  const { getValues, setValue } = useFormContext();

  useEffect(() => {
    if (!enabled) return;

    if (qualifiedGroupType === 'medical') {
      if (!getValues(`${name}.verification_type`)) {
        setValue(`${name}.verification_type`, 'photo');
      }
      return;
    }

    if (qualifiedGroupType) {
      setValue(`${name}.verification_type`, null);
    }
  }, [enabled, getValues, name, qualifiedGroupType, setValue]);
};

// Hydrates the `ruleSettings.*` form namespace from rules. This bucket holds the
// per-type scalar settings (target_weight, thresholds, qualified-group type/age,
// bundle settings); rules are the only data source and it is stripped before submit.
export const getRuleSettingsFormState = (
  special?: StoreSpecialV2 | GlobalSpecialV2
): RuleSettingsFormState => {
  if (!special?.special_type) {
    return {};
  }

  const rules = special.rules;

  switch (special.special_type) {
    case 'cart_total':
      return { cart_total: { threshold: rules?.settings?.cart_threshold } };
    case 'qualified_group':
      return {
        qualified_group: {
          type: rules?.base?.type,
          required_age: rules?.base?.required_age,
          verification_type:
            rules?.base?.verification_type ||
            (rules?.base?.type === 'medical' ? 'photo' : undefined),
        },
      };
    case 'bulk_pricing':
      return {
        bulk_pricing: {
          target_weight: rules?.settings?.target_weight,
          max_applications_per_cart: rules?.settings?.max_applications_per_cart,
        },
      };
    case 'bundle':
      return {
        bundle: {
          max_applications_per_cart: rules?.settings?.max_applications_per_cart,
          settings: pick(rules?.settings, [
            'allow_discounts_on_required_products',
          ]),
        },
      };
    default:
      return {};
  }
};

/** Whether the discounted products reuse the required products' conditions */
const isCopyingDiscountedConditions = (
  specialType: SpecialType | '' | undefined,
  display?: Partial<SpecialFormDisplay>
) => {
  if (specialType === 'bundle') {
    return (
      display?.bundle?.settings?.copy_conditions_for_discounted_products ===
      true
    );
  }
  if (specialType === 'spending_threshold') {
    return (
      display?.spending_threshold?.copy_conditions_for_discounted_products ===
      true
    );
  }
  return false;
};

const hasConditions = ({ selections }: ConditionSet) =>
  Object.keys(selections.includes).length > 0 ||
  Object.keys(selections.excludes).length > 0;

/**
 * Total condition groups across the editors the user fills in. Copying the
 * required conditions hides the discounted editor, whose groups are then
 * derived from the qualifier's on submit rather than entered.
 *
 * Empty groups are skipped unless `includeEmpty`, ex when tracking a group
 * that was just added and is necessarily blank.
 */
export const getConditionGroupCount = ({
  conditionSets,
  display,
  includeEmpty = false,
  specialType,
}: {
  conditionSets: ConditionSetsByKey;
  display?: Partial<SpecialFormDisplay>;
  includeEmpty?: boolean;
  specialType?: SpecialType | '';
}) => {
  const copying = isCopyingDiscountedConditions(specialType, display);

  return getConditionEditors(specialType)
    .filter(({ rulesKey }) => !(copying && rulesKey === 'discountable'))
    .reduce((count, { rulesKey }) => {
      const sets = conditionSets[rulesKey];

      return count + (includeEmpty ? sets : sets.filter(hasConditions)).length;
    }, 0);
};

export const getRules = (
  isCreateMode: boolean,
  special?: StoreSpecialV2 | GlobalSpecialV2
) => {
  if (isCreateMode) {
    return {
      required_rules: {
        apply_to: RULE_OPTIONS[2].value,
        rules: {},
      },
      default_rules: {
        rules: {},
        apply_to: RULE_OPTIONS[2].value,
      },
      discounted_rules: {
        apply_to: RULE_OPTIONS[2].value,
        rules: {},
      },
    };
  }

  if (special?.special_type === 'bundle') {
    // Bundle rules share the qualifier/discountable shape with spending_threshold
    return convertSpendingThresholdToRules(special.rules);
  }
  if (special?.special_type === 'bulk_pricing') {
    // settings (target_weight etc) live outside the conditions UI's rule state
    const rules = transformRules(omit(special?.rules, ['settings']));

    return {
      default_rules: {
        rules,
        apply_to: getApplyToSelection(rules),
      },
    };
  }
  if (special?.special_type === 'spending_threshold') {
    const rules = special?.rules;

    return convertSpendingThresholdToRules(rules);
  } else {
    return {
      default_rules: {
        rules: transformRules(special?.rules),
        apply_to: getApplyToSelection(special?.rules),
      },
    };
  }
};

/**
 * Used when rendering the form, will return which "Apply To" radio selection should be set based on the special rules.
 * @param rules SpecialRulesV2
 * @returns One of the RULE_OPTIONS values
 */
export const getApplyToSelection = (rules?: SpecialRulesV2 | null) => {
  const defaultValue = RULE_OPTIONS[2].value;

  if (!rules) {
    return defaultValue;
  }

  const excludes = rules['excludes'] || [];
  const includes = rules['includes'] || [];
  const hasExclusions = !isEmpty(excludes);
  const hasInclusions = !isEmpty(includes);
  const firstExclusion = excludes[0] || [];
  const firstInclusion = includes[0] || [];

  const hasAdvancedInclusions =
    !isEmpty(firstInclusion['kinds']) ||
    !isEmpty(firstInclusion['brands']) ||
    !isEmpty(firstInclusion['lineages']);
  const hasAdvancedExclusions =
    !isEmpty(firstExclusion['kinds']) || !isEmpty(firstExclusion['brands']);

  // Has product inclusions, should be INDIVIDUAL
  if (hasInclusions && !isEmpty(firstInclusion['product_ids'])) {
    return RULE_OPTIONS[1].value;
    // Has category or brands or lineage inclusions, or category or brands exclusions, should be CONDITIONS
  } else if (
    (hasInclusions && hasAdvancedInclusions) ||
    (hasExclusions && hasAdvancedExclusions)
  ) {
    return RULE_OPTIONS[2].value;
  } else {
    return defaultValue;
  }
};

// Mirrors api/app/models/inventory.rb PRICE_IDS_TO_WEIGHT. Used to derive
// bulk_weight_values from the legacy bulk_weights shape on save so the rules
// payload carries both keys (backend `copy_weight_conditions` does the same
// translation for conditions, but does not touch the rules payload for
// bulk_pricing — without mirroring here, the saved rules column would only
// hold the legacy bulk_weights key).
const PRICE_ID_TO_WEIGHT: Record<ProductWeight, number> = {
  half_gram: 0.5,
  gram: 1.0,
  two_gram: 2.0,
  eighth_ounce: 3.5,
  quarter_ounce: 7,
  half_ounce: 14,
  ounce: 28,
};

const mirrorBulkWeightValues = <
  T extends { excludes?: any[]; includes?: any[] },
>(
  result: T
): T => {
  for (const arr of [result.includes, result.excludes]) {
    if (!arr) continue;
    for (const entry of arr) {
      const bulkWeights = entry?.bulk_weights;
      if (!Array.isArray(bulkWeights) || bulkWeights.length === 0) continue;

      entry.bulk_weight_values = bulkWeights.map(
        (rule: {
          maximum_price?: number;
          minimum_price?: number;
          price_id: ProductWeight;
        }) => {
          const next: Record<string, unknown> = {
            weight_value: {
              units: 'g',
              value: PRICE_ID_TO_WEIGHT[rule.price_id],
            },
          };
          if (rule.minimum_price !== undefined)
            next['minimum_price'] = rule.minimum_price;
          if (rule.maximum_price !== undefined)
            next['maximum_price'] = rule.maximum_price;
          return next;
        }
      );
    }
  }
  return result;
};

// Derive the weight_values form of a legacy `weights` (price-id) selection,
// dropping any price_id without a known gram equivalent (mirrors the backend's
// Inventory::PRICE_IDS_TO_WEIGHT filter_map in Specials::Normalize).
const deriveWeightValues = (weights: ProductWeight[]) =>
  weights
    .filter((priceId) => PRICE_ID_TO_WEIGHT[priceId] !== undefined)
    .map((priceId) => ({ units: 'g', value: PRICE_ID_TO_WEIGHT[priceId] }));

// The backend is weight_values-native: Specials::Normalize#normalize_weights!
// keeps weight_values and only backfills it from weights when absent
// (`weight_values ||= ...`). So weight_values must always be present and fresh,
// otherwise a stale value carried over from the loaded special silently wins
// over the user's new weights selection.
//
// - Legacy UI (scWeightValuesFrontend off) edits `weights`; always emit a fresh
//   weight_values derived from it, and keep `weights` for backward compat.
// - scWeightValuesFrontend on: the UI edits `weight_values` directly and we drop
//   the legacy `weights` key entirely (the rollout's end state).
const normalizeEntryWeightKeys = (
  entry: Record<string, any>,
  weightValuesEnabled: boolean
): Record<string, any> => {
  const next = { ...entry };

  if (weightValuesEnabled) {
    delete next['weights'];
    if (
      !Array.isArray(next['weight_values']) ||
      next['weight_values'].length === 0
    ) {
      delete next['weight_values'];
    }
    return next;
  }

  const weights = next['weights'];
  if (Array.isArray(weights) && weights.length > 0) {
    next['weight_values'] = deriveWeightValues(weights);
  } else {
    // No weight selection: never leave a stale weight_values behind.
    delete next['weights'];
    delete next['weight_values'];
  }
  return next;
};

// Final pass shared by every apply-to branch. Bulk specials carry their weight
// data on bulk_weights/bulk_weight_values (handled by mirrorBulkWeightValues);
// every other special type runs each includes/excludes rule through
// normalizeEntryWeightKeys so the persisted rules are always weight_values-fresh.
const finalizeRules = <T extends { excludes?: any[]; includes?: any[] }>(
  result: T,
  isBulk: boolean,
  weightValuesEnabled: boolean
): T => {
  if (isBulk) {
    return mirrorBulkWeightValues(result);
  }
  for (const key of ['includes', 'excludes'] as const) {
    const arr = result[key];
    if (Array.isArray(arr)) {
      (result as Record<string, any>)[key] = arr.map((entry) =>
        normalizeEntryWeightKeys(entry, weightValuesEnabled)
      );
    }
  }
  return result;
};

/**
 * Used when saving the form, will return a rules object based on which "Apply To" selection was made.
 *
 * The output shape mirrors the backend dry-schema in
 * api/app/models/rules/validate/types.rb: bulk_pricing emits BulkPricingRulesPayload
 * (with bulk_weights and a derived bulk_weight_values), every other
 * specialType emits SpecialRulesPayload (with weights/weight_values). Inclusion/exclusion
 * keys are whitelisted per specialType — any unknown keys on the form-state
 * input are dropped. Exclusions made redundant by inclusions are folded away.
 */
export function parseRulesFromApplyTo<T extends SpecialTypeWithRules>(
  rules: FormRules,
  applyTo: ApplyToOptions,
  weightValuesEnabled: boolean,
  specialType: T
): SpecialRulesPayloadFor<T>;
export function parseRulesFromApplyTo(
  formRules: FormRules,
  applyTo: ApplyToOptions,
  weightValuesEnabled: boolean,
  specialType: SpecialTypeWithRules
): SpecialRulesPayload | BulkPricingRulesPayload {
  const rules = foldRedundantExclusions(formRules);
  const isBulk = specialType === 'bulk_pricing';
  const weightKey = weightValuesEnabled ? 'weight_values' : 'weights';
  const ruleKeys = isBulk ? BULK_PRICING_RULE_KEYS : RULE_KEYS;

  if (applyTo === 'CONDITIONS') {
    const sanitized: FormRules = sanitizeRules(rules) ?? {};
    const includes = (sanitized.includes ?? []).map((inc: FormInclusion) =>
      pick(omit(inc, 'product_ids'), ruleKeys as readonly string[])
    );
    const excludes = (sanitized.excludes ?? []).map((exc: FormExclusion) =>
      pick(exc, ruleKeys as readonly string[])
    );
    const result: Record<string, unknown> = { includes, excludes };
    if (sanitized.product_threshold) {
      result['product_threshold'] = sanitized.product_threshold;
    }
    return finalizeRules(
      result as { excludes: any[]; includes: any[] },
      isBulk,
      weightValuesEnabled
    );
  }

  const bulkIncludeWeights = isBulk && get(rules, 'includes.0.bulk_weights');

  const includesEntry: Record<string, any> =
    (get(rules, 'includes.0') as Record<string, any>) ?? {};
  const excludesEntry: Record<string, any> =
    (get(rules, 'excludes.0') as Record<string, any>) ?? {};
  const productThreshold: ProductThreshold | undefined = rules.product_threshold
    ? {
        minimum_price: rules.product_threshold.minimum_price ?? undefined,
        maximum_price: rules.product_threshold.maximum_price ?? undefined,
      }
    : undefined;

  if (applyTo === 'ALL') {
    const includes: Record<string, any> = isBulk
      ? {}
      : pick(includesEntry, [weightKey]);
    const excludes = pick(excludesEntry, ['product_ids']);

    const built = buildRules({
      excludeProduct: excludes['product_ids'],
      includeBulkWeights: bulkIncludeWeights,
      includeWeightValue:
        !isBulk && weightValuesEnabled && includes['weight_values'],
      includeWeight: !isBulk && !weightValuesEnabled && includes['weights'],
    });
    const allResult = {
      includes: built.includes ?? [],
      excludes: built.excludes ?? [],
      product_threshold: productThreshold,
    };
    return finalizeRules(allResult, isBulk, weightValuesEnabled);
  }

  // applyTo === 'INDIVIDUAL'
  const indKeys = isBulk ? ['product_ids'] : [weightKey, 'product_ids'];
  const includes = pick(includesEntry, indKeys);
  const excludes = pick(excludesEntry, indKeys);

  const built = buildRules({
    includeProduct: includes['product_ids'],
    excludeProduct: excludes['product_ids'],
    includeBulkWeights: bulkIncludeWeights,
    excludeBulkWeights: isBulk && get(rules, 'excludes.0.bulk_weights'),
    includeWeight: !isBulk && !weightValuesEnabled && includes['weights'],
    excludeWeight: !isBulk && !weightValuesEnabled && excludes['weights'],
    includeWeightValue:
      !isBulk && weightValuesEnabled && includes['weight_values'],
    excludeWeightValue:
      !isBulk && weightValuesEnabled && excludes['weight_values'],
  });
  const individualResult = {
    includes: built.includes ?? [],
    excludes: built.excludes ?? [],
    product_threshold: productThreshold,
  };
  return finalizeRules(individualResult, isBulk, weightValuesEnabled);
}

export const useTriggerDuplicateValidation = (
  mutationKey: any,
  specialIsLoading: boolean,
  isDuplicateMode: boolean,
  trigger: any
) => {
  const isMutating = useIsMutating({
    mutationKey,
  });
  const [hasDuplicationErrors, setHasDuplicationErrors] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isDuplicateMode && !specialIsLoading && !isMutating) {
      // Slight delay to make sure form is loaded and rendered
      timeoutRef.current = setTimeout(async () => {
        const initialErrors = await trigger(undefined, { shouldFocus: true });
        setHasDuplicationErrors(!initialErrors);
      }, 500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [specialIsLoading, isDuplicateMode, isMutating]);

  return hasDuplicationErrors;
};

/**
 * Used when rendering the form and validation is needed before the user interacts with the form
 * @param mutationKey validated form field
 * @param specialIsLoading boolean indicating if the special is still loading
 * @param isError booelean indicating if the form has a validation error defined by the caller
 * @param trigger react-hook-form method that
 * @returns boolean indicating if the form has validation errors
 */
export const useTriggerFormValidation = (
  mutationKey: any,
  specialIsLoading: boolean,
  isError: boolean,
  trigger: any
) => {
  const isMutating = useIsMutating({
    mutationKey,
  });
  const [hasFormError, setHasFormError] = useState(false);
  useEffect(() => {
    if (isError && !specialIsLoading && !isMutating) {
      // Slight delay to make sure form is loaded and rendered
      setTimeout(async () => {
        const initialErrors = await trigger(undefined, { shouldFocus: true });
        setHasFormError(!initialErrors);
      }, 500);
    }
  }, [specialIsLoading, isError, isMutating]);

  return hasFormError;
};

// Partially copied from libs/shared/util/src/lib/specials/getSpecialTagLabel.ts (formatDiscountAmount)
export const formatDiscountPlaceholder = ({
  discount_amount,
  discount_percent,
  discount_target_price,
  discount_type,
}: {
  discount_amount: string;
  discount_percent: string;
  discount_target_price: string;
  discount_type: string;
}) => {
  const formatPrice = (price = '0') => {
    const floatPrice = parseFloat(price);
    return floatPrice % 1 === 0 ? floatPrice.toFixed(0) : floatPrice.toFixed(2);
  };

  switch (discount_type) {
    case 'percent':
      return discount_percent && parseFloat(discount_percent) !== 0
        ? `${parseFloat(discount_percent).toString()}% OFF`
        : 'SALE';
    case 'dollar_amount':
      return discount_amount && parseFloat(discount_amount) !== 0
        ? `$${formatPrice(discount_amount)} OFF`
        : 'SALE';
    case 'target_price':
      return `$${formatPrice(discount_target_price)} (TARGET WEIGHT)`;
    default:
      return 'SALE';
  }
};

/** New Conditions Helpers */
// qualifier/discountable accept either a single ruleset (spending_threshold's native form)
// or an array of rulesets (bundle parity payload)
// coerce defensively in case an array slips through.
const firstRuleset = <T>(value: T | T[] | null | undefined): T | undefined =>
  Array.isArray(value) ? value[0] : (value ?? undefined);

export const convertSpendingThresholdToRules = (
  spendingRules: SpecialRulesV2
) => {
  if (spendingRules) {
    const qualifier = firstRuleset(spendingRules.qualifier);
    const discountable = firstRuleset(spendingRules.discountable);
    return {
      required_rules: {
        apply_to: getApplyToSelection(qualifier),
        rules: transformRules(qualifier || {}),
      },
      discounted_rules: {
        apply_to: getApplyToSelection(discountable),
        rules: transformRules(discountable || {}),
      },
    };
  }
  return;
};
